import { Component, Inject, OnInit, DOCUMENT } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms'
import { AuthService } from '@shared/services/auth.service'
import { environment } from '@public/app/environments/environment'
import { ToastService } from '../../services/toast.service'
import { NOT_A_PASSWORD_RESET_LINK, passwordResetErrorMessage } from './password-reset-error-message'

export const MIN_PASSWORD_LENGTH = 6

@Component({
  selector: 'app-reset-password',
  templateUrl: './reset-password.component.html',
  styleUrls: ['./reset-password.component.scss'],
  standalone: false,
})
export class ResetPasswordComponent implements OnInit {
  resetPasswordForm: UntypedFormGroup
  oobCode: string | null = null
  email: string | null = null
  linkError: string | null = null
  saving = false
  pageTitle = 'Jelszó visszaállítása'
  private continueUrl: string | null = null

  constructor(
    private route: ActivatedRoute,
    private authService: AuthService,
    private toastService: ToastService,
    private router: Router,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.resetPasswordForm = new UntypedFormGroup({
      password: new UntypedFormControl('', [Validators.required, Validators.minLength(MIN_PASSWORD_LENGTH)]),
      confirmPassword: new UntypedFormControl('', Validators.required),
    })
  }

  get passwordError(): string {
    const password = this.resetPasswordForm.get('password')
    if (!password?.touched) {
      return ''
    }
    if (password.hasError('required')) {
      return 'A jelszó megadása kötelező'
    }
    return password.hasError('minlength') ? `A jelszó legalább ${MIN_PASSWORD_LENGTH} karakter legyen.` : ''
  }

  get confirmPasswordError(): string {
    const confirmPassword = this.resetPasswordForm.get('confirmPassword')
    return confirmPassword?.touched && confirmPassword.invalid ? 'A jelszó megerősítése kötelező' : ''
  }

  ngOnInit(): void {
    const { queryParamMap, paramMap } = this.route.snapshot
    this.oobCode = queryParamMap.get('oobCode') ?? paramMap.get('oobCode')
    this.continueUrl = queryParamMap.get('continueUrl')
    if (!this.oobCode) {
      this.router.navigate(['/login'])
      return
    }
    const mode = queryParamMap.get('mode')
    if (mode && mode !== 'resetPassword') {
      this.linkError = NOT_A_PASSWORD_RESET_LINK
      return
    }
    this.authService.verifyPasswordResetCode(this.oobCode).then(
      email => (this.email = email),
      error => (this.linkError = passwordResetErrorMessage(error))
    )
  }

  async onSubmit(): Promise<void> {
    if (this.resetPasswordForm.invalid || !this.oobCode || !this.email || this.saving) {
      return
    }

    const { password, confirmPassword } = this.resetPasswordForm.value
    if (password !== confirmPassword) {
      this.toastService.showError('A jelszavak nem egyeznek.')
      return
    }

    this.saving = true
    try {
      await this.authService.confirmPasswordReset(this.oobCode, password)
    } catch (error) {
      this.saving = false
      this.toastService.showError(passwordResetErrorMessage(error))
      return
    }

    const adminLogin = this.adminContinueUrl()
    if (adminLogin) {
      this.toastService.showSuccess('A jelszavad megváltozott. Lépj be az admin felületre az új jelszavaddal.')
      this.document.location.assign(adminLogin)
      return
    }

    try {
      await this.authService.signInWithEmailAndPassword(this.email, password)
      this.toastService.showSuccess('A jelszavad megváltozott. Sikeresen bejelentkeztél.')
      this.router.navigate(['/'])
    } catch {
      this.toastService.showSuccess('A jelszavad megváltozott. Lépj be az új jelszavaddal.')
      this.router.navigate(['/login'])
    }
  }

  private adminContinueUrl(): string | null {
    if (!this.continueUrl) {
      return null
    }
    try {
      const { origin } = new URL(this.continueUrl)
      const isAdmin = origin !== this.document.location.origin && environment.adminOrigins.includes(origin)
      return isAdmin ? this.continueUrl : null
    } catch {
      return null
    }
  }
}
